// ═══════════════════════════════════════════════════════════════
// DISPUTE SERVICE — Client wrappers for lost items + damage claims (Batch 14)
// ═══════════════════════════════════════════════════════════════

import { httpsCallable } from 'firebase/functions';
import { collection, doc, getDoc, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { db, functions } from '../config/firebase';

// ───────── Lost Items: user-facing wrappers ─────────

export async function reportLostItem({ bookingId, description, locationInVehicle, stagedPhotoUrls }) {
  const fn = httpsCallable(functions, 'reportLostItem');
  return (await fn({ bookingId, description, locationInVehicle, stagedPhotoUrls })).data;
}

export async function reportFoundItem({ bookingId, description, locationInVehicle, notes, stagedPhotoUrls }) {
  const fn = httpsCallable(functions, 'reportFoundItem');
  return (await fn({ bookingId, description, locationInVehicle, notes, stagedPhotoUrls })).data;
}

export async function driverRespondToLostItem({ caseId, response, notes, stagedPhotoUrls }) {
  const fn = httpsCallable(functions, 'driverRespondToLostItem');
  return (await fn({ caseId, response, notes, stagedPhotoUrls })).data;
}

export async function riderOfferReturnFee({ caseId, fee }) {
  const fn = httpsCallable(functions, 'riderOfferReturnFee');
  return (await fn({ caseId, fee })).data;
}

export async function driverConfirmReturnFee({ caseId, accept }) {
  const fn = httpsCallable(functions, 'driverConfirmReturnFee');
  return (await fn({ caseId, accept })).data;
}

export async function driverMarkReturned({ caseId, notes }) {
  const fn = httpsCallable(functions, 'driverMarkReturned');
  return (await fn({ caseId, notes })).data;
}

export async function riderConfirmReceipt({ caseId }) {
  const fn = httpsCallable(functions, 'riderConfirmReceipt');
  return (await fn({ caseId })).data;
}

export async function riderRequestReturnRefund({ caseId, reason }) {
  const fn = httpsCallable(functions, 'riderRequestReturnRefund');
  return (await fn({ caseId, reason })).data;
}

export async function riderCancelLostItemReport({ caseId, reason }) {
  const fn = httpsCallable(functions, 'riderCancelLostItemReport');
  return (await fn({ caseId, reason })).data;
}

// ───────── Lost Items: admin ─────────

export async function adminOverrideLostItem({ caseId, newStatus, reason, refundRider, payoutDriver }) {
  const fn = httpsCallable(functions, 'adminOverrideLostItem');
  return (await fn({ caseId, newStatus, reason, refundRider, payoutDriver })).data;
}

// ───────── Damage Claims ─────────

export async function submitDamageClaim({ bookingId, type, description, amount, stagedPhotoUrls, stagedReceiptUrls }) {
  const fn = httpsCallable(functions, 'submitDamageClaim');
  return (await fn({ bookingId, type, description, amount, stagedPhotoUrls, stagedReceiptUrls })).data;
}

export async function withdrawDamageClaim({ claimId, reason }) {
  const fn = httpsCallable(functions, 'withdrawDamageClaim');
  return (await fn({ claimId, reason })).data;
}

export async function reviewDamageClaim({ claimId, decision, approvedAmount, note }) {
  const fn = httpsCallable(functions, 'reviewDamageClaim');
  return (await fn({ claimId, decision, approvedAmount, note })).data;
}

export async function retryDamageClaimCharge({ claimId }) {
  const fn = httpsCallable(functions, 'retryDamageClaimCharge');
  return (await fn({ claimId })).data;
}

// ───────── Config ─────────

let _disputeConfigCache = null;
export async function getDisputeConfig() {
  if (_disputeConfigCache) return _disputeConfigCache;
  const fn = httpsCallable(functions, 'getDisputeConfig');
  _disputeConfigCache = (await fn({})).data;
  return _disputeConfigCache;
}

// ───────── Direct Firestore queries (read-only) ─────────

// Get a single lost item case
export async function getLostItemCase(caseId) {
  const snap = await getDoc(doc(db, 'lostItems', caseId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Get all lost item cases for a rider or driver
export async function getMyLostItemCases(userId, role) {
  const field = role === 'driver' ? 'driverId' : 'riderId';
  const q = query(
    collection(db, 'lostItems'),
    where(field, '==', userId),
    orderBy('createdAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Get a single damage claim
export async function getDamageClaim(claimId) {
  const snap = await getDoc(doc(db, 'claims', claimId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

// Get all damage claims for a driver or rider
export async function getMyDamageClaims(userId, role) {
  const field = role === 'driver' ? 'driverId' : 'riderId';
  const q = query(
    collection(db, 'claims'),
    where(field, '==', userId),
    orderBy('submittedAt', 'desc'),
    limit(50)
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

// Helper: rider's recent eligible bookings for lost item reporting
// Returns completed bookings within the 14-day window
export async function getEligibleBookingsForLostItemReport(riderId) {
  const cutoff = new Date(Date.now() - 14 * 86400000);
  const q = query(
    collection(db, 'bookings'),
    where('riderId', '==', riderId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => {
      const c = b.completedAt?.toDate?.() || (b.completedAt ? new Date(b.completedAt) : null);
      return c && c >= cutoff;
    });
}

// Helper: driver's recent eligible bookings for damage claim
// Returns completed bookings within the 48-hour window
export async function getEligibleBookingsForDamageClaim(driverId) {
  const cutoff = new Date(Date.now() - 48 * 3600000);
  const q = query(
    collection(db, 'bookings'),
    where('driverId', '==', driverId),
    where('status', '==', 'completed'),
    orderBy('completedAt', 'desc'),
    limit(20)
  );
  const snap = await getDocs(q);
  return snap.docs
    .map(d => ({ id: d.id, ...d.data() }))
    .filter(b => {
      const c = b.completedAt?.toDate?.() || (b.completedAt ? new Date(b.completedAt) : null);
      return c && c >= cutoff;
    });
}

// ───────── Display helpers ─────────

// Map status code to user-friendly label + tone
export function lostItemStatusBadge(status) {
  const map = {
    reported: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting driver' },
    driver_found_will_return: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Driver has item' },
    driver_found_needs_pickup: { bg: '#dbeafe', color: '#1e40af', label: 'Pickup needed' },
    driver_not_found: { bg: '#fee2e2', color: '#991b1b', label: 'Not found' },
    awaiting_return_fee: { bg: '#fef3c7', color: '#92400e', label: 'Offer return fee' },
    awaiting_driver_fee_confirmation: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting driver' },
    return_paid: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Return authorized' },
    return_in_progress: { bg: '#dbeafe', color: '#1e40af', label: 'Driver en route' },
    return_delivered_by_driver: { bg: '#fef3c7', color: '#92400e', label: 'Confirm receipt' },
    awaiting_pickup_coordination: { bg: '#dbeafe', color: '#1e40af', label: 'Coordinate pickup' },
    pickup_booking_made: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Pickup booked' },
    resolved_returned: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Returned' },
    resolved_collected: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Collected' },
    unresolved: { bg: '#fee2e2', color: '#991b1b', label: 'Unresolved' },
    expired_driver_custody: { bg: '#f1f5f9', color: '#5a6578', label: 'Custody expired' },
    unresolved_pickup_timeout: { bg: '#fee2e2', color: '#991b1b', label: 'Pickup timeout' },
    cancelled_by_rider: { bg: '#f1f5f9', color: '#5a6578', label: 'Cancelled' },
    admin_resolved: { bg: '#e0e7ff', color: '#4338ca', label: 'Admin resolved' },
    admin_review_refund: { bg: '#fef3c7', color: '#92400e', label: 'Refund review' },
  };
  return map[status] || { bg: '#f1f5f9', color: '#5a6578', label: status };
}

export function damageClaimStatusBadge(status) {
  const map = {
    submitted: { bg: '#fef3c7', color: '#92400e', label: 'Awaiting review' },
    admin_reviewing: { bg: '#dbeafe', color: '#1e40af', label: 'Under review' },
    awaiting_charge: { bg: '#fef3c7', color: '#92400e', label: 'Charging rider' },
    charge_failed: { bg: '#fee2e2', color: '#991b1b', label: 'Charge failed' },
    approved_paid: { bg: 'rgba(45,212,168,0.1)', color: '#059669', label: 'Approved & paid' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected' },
    withdrawn: { bg: '#f1f5f9', color: '#5a6578', label: 'Withdrawn' },
  };
  return map[status] || { bg: '#f1f5f9', color: '#5a6578', label: status };
}

export const LOCATION_IN_VEHICLE_OPTIONS = [
  { value: 'back_seat', label: 'Back seat' },
  { value: 'front_seat', label: 'Front passenger seat' },
  { value: 'boot', label: 'Boot / trunk' },
  { value: 'door_pocket', label: 'Door pocket' },
  { value: 'floor', label: 'On the floor' },
  { value: 'other', label: 'Other / not sure' },
];